import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

const FALLBACK =
  "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=1400&q=80";

function BuildCard({ build }) {
  const image =
    mediaUrl(build.cover_image || build.images?.[0]?.url) || FALLBACK;
  return (
    <Link
      to={`/builds/${build.id}`}
      className="group block sa-card overflow-hidden"
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#171717]">
        <img
          src={image}
          alt={`${build.make} ${build.model}`}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-5">
        <div className="overline text-[#E63946]">
          {build.title || "Complete Transformation"}
        </div>
        <h3 className="font-display text-2xl mt-2">
          {build.make} {build.model}
        </h3>
        <p className="text-sm text-white/60 mt-2 line-clamp-2">
          {build.description}
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {(build.categories || []).slice(0, 4).map((c) => (
            <span
              key={c}
              className="text-[10px] uppercase tracking-wider border border-white/15 px-2 py-1 text-white/60"
            >
              {c}
            </span>
          ))}
        </div>
        <div className="overline mt-5 flex items-center gap-2">
          VIEW BUILD <ArrowRight size={14} className="text-[#E63946]" />
        </div>
      </div>
    </Link>
  );
}

export function Builds() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api
      .get("/builds")
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_10%,rgba(230,57,70,.22),transparent_45%)]" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="overline text-[#E63946] mb-5">
            Street Auto Archives
          </div>
          <h1 className="font-display text-5xl md:text-8xl leading-[.88] max-w-4xl">
            REAL CARS.
            <br />
            <span className="text-[#E63946]">REAL TRANSFORMATIONS.</span>
          </h1>
          <p className="mt-7 text-white/60 max-w-xl text-lg">
            Take inspiration from cars we've already transformed. Explore the
            details behind each project.
          </p>
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-white/40">Our builds</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">
              THE GARAGE
            </h2>
          </div>
          <Link to="/build" className="sa-btn sa-btn-primary">
            START YOUR BUILD
          </Link>
        </div>
        {items.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((b) => (
              <BuildCard key={b.id} build={b} />
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/15 py-20 text-center text-white/50">
            Our next transformation is being documented. Start your build and be
            the first feature.
          </div>
        )}
      </section>
    </div>
  );
}

export function BuildDetail() {
  const { id } = useParams();
  const [build, setBuild] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useEffect(() => {
    api
      .get(`/builds/${id}`)
      .then((r) => setBuild(r.data))
      .catch(() => setBuild(false));
  }, [id]);
  if (build === false)
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-24">
        <h1 className="font-display text-4xl">BUILD NOT FOUND</h1>
        <Link to="/builds" className="sa-btn sa-btn-primary mt-6">
          BACK TO BUILDS
        </Link>
      </div>
    );
  if (!build)
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-24 text-white/50">
        Loading build...
      </div>
    );
  const images = build.images?.length
    ? [...build.images].sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0),
      )
    : [{ url: build.cover_image || FALLBACK, image_type: "general" }];
  const current = images[active] || images[0];
  const move = (delta) =>
    setActive((active + delta + images.length) % images.length);
  return (
    <div className="min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <Link
          to="/builds"
          className="overline text-white/50 flex items-center gap-2"
        >
          <ArrowLeft size={14} /> OUR BUILDS
        </Link>
        <div className="grid lg:grid-cols-[1.2fr_.8fr] gap-8 lg:gap-14 mt-8">
          <div>
            <button
              onClick={() => setLightbox(true)}
              className="block w-full text-left relative aspect-[4/3] bg-[#111] overflow-hidden"
            >
              <img
                src={mediaUrl(current.url)}
                alt={current.caption || `${build.make} ${build.model}`}
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-4 right-4 bg-black/70 px-3 py-2 overline">
                {active + 1} / {images.length}
              </span>
            </button>
            <div className="flex gap-2 overflow-x-auto mt-3 pb-1">
              {images.map((img, index) => (
                <button
                  key={img.id || index}
                  onClick={() => setActive(index)}
                  className={`shrink-0 w-20 h-16 border-2 ${index === active ? "border-[#E63946]" : "border-transparent"}`}
                >
                  <img
                    src={mediaUrl(img.url)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="py-2">
            <div className="overline text-[#E63946]">
              {build.title || "Complete Transformation"}
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[.9] mt-4">
              {build.make}
              <br />
              {build.model}
            </h1>
            {build.year && (
              <div className="text-white/50 mt-4">{build.year}</div>
            )}
            <p className="text-white/65 leading-relaxed mt-7">
              {build.description}
            </p>
            <div className="flex flex-wrap gap-2 mt-7">
              {build.categories?.map((c) => (
                <span
                  key={c}
                  className="border border-white/15 px-3 py-2 text-xs uppercase tracking-wider"
                >
                  {c}
                </span>
              ))}
            </div>
            <Link to="/build" className="sa-btn sa-btn-primary mt-9">
              START MY BUILD <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-16 border-t border-white/10 pt-10">
          {[
            ["Cosmetic modifications", build.cosmetic_modifications],
            ["Interior modifications", build.interior_modifications],
            ["Performance modifications", build.performance_modifications],
            ["ECU / remap work", build.ecu_work],
            ["Other work", build.other_work],
            [
              "Project details",
              [build.project_duration, build.location]
                .filter(Boolean)
                .join(" · "),
            ],
          ]
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label} className="bg-[#111] border border-white/10 p-5">
                <div className="overline text-[#E63946]">{label}</div>
                <p className="text-sm text-white/70 mt-3 whitespace-pre-line">
                  {value}
                </p>
              </div>
            ))}
        </div>
        <section className="mt-20 py-14 px-6 md:px-12 border border-[#E63946]/40 bg-[#160b0c] flex flex-col md:flex-row items-start md:items-center justify-between gap-7">
          <div>
            <div className="overline text-[#E63946]">Like this build?</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">
              MAKE YOURS NEXT.
            </h2>
            <p className="text-white/60 mt-3 max-w-xl">
              Want something similar for your car? Tell us what you're looking
              for and we'll create a modification plan around your car and
              budget.
            </p>
          </div>
          <Link to="/build" className="sa-btn sa-btn-primary shrink-0">
            START MY BUILD
          </Link>
        </section>
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-5 right-5 p-3"
            aria-label="Close"
          >
            <X />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              move(-1);
            }}
            className="absolute left-3 md:left-8 p-3 bg-white/10"
            aria-label="Previous"
          >
            <ChevronLeft />
          </button>
          <img
            onClick={(e) => e.stopPropagation()}
            src={mediaUrl(current.url)}
            alt={current.caption || "Build detail"}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              move(1);
            }}
            className="absolute right-3 md:right-8 p-3 bg-white/10"
            aria-label="Next"
          >
            <ChevronRight />
          </button>
          <div className="absolute bottom-5 overline">
            {active + 1} / {images.length}{" "}
            {current.caption && ` · ${current.caption}`}
          </div>
        </div>
      )}
    </div>
  );
}

const services = [
  "Exterior",
  "Interior",
  "Wheels & Tyres",
  "Lighting",
  "Wrap / Paint",
  "Suspension",
  "Exhaust",
  "Performance",
  "ECU Remap",
  "Complete Transformation",
  "Other",
];
const budgets = [
  "Under ₹50,000",
  "₹50,000 – ₹1,00,000",
  "₹1,00,000 – ₹2,00,000",
  "₹2,00,000 – ₹5,00,000",
  "₹5,00,000+",
  "I'm not sure / Need advice",
];

export function BuildEnquiry() {
  const [step, setStep] = useState(1);
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    services: [],
    vehicle_images: [],
    inspiration_images: [],
  });
  const set = (key, value) => setF((old) => ({ ...old, [key]: value }));
  const files = async (key, event) => {
    const endpoint =
      key === "vehicle_images"
        ? "/build-enquiry-images"
        : "/build-enquiry-images";
    const uploaded = [];
    for (const file of Array.from(event.target.files || [])) {
      const body = new FormData();
      body.append("file", file);
      const response = await api.post(endpoint, body);
      uploaded.push(response.data.url);
    }
    set(key, [...(f[key] || []), ...uploaded]);
  };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/build-enquiries", f);
      setSent(true);
    } finally {
      setSaving(false);
    }
  };
  if (sent)
    return (
      <div className="max-w-2xl mx-auto px-4 py-28 text-center">
        <div className="w-16 h-16 bg-[#E63946] flex items-center justify-center mx-auto">
          <Check size={30} />
        </div>
        <div className="overline text-[#E63946] mt-7">
          Build request received
        </div>
        <h1 className="font-display text-4xl md:text-6xl mt-3">
          WE'LL TAKE IT FROM HERE.
        </h1>
        <p className="text-white/60 mt-5">
          Street Auto Accessories will review your requirements and contact you
          for a technical consultation. There is no automatic final price at
          this stage.
        </p>
        <Link to="/builds" className="sa-btn sa-btn-outline mt-8">
          EXPLORE OUR BUILDS
        </Link>
      </div>
    );
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20">
      <Link to="/" className="overline text-white/50 flex items-center gap-2">
        <ArrowLeft size={14} /> STREET AUTO
      </Link>
      <div className="mt-8">
        <div className="overline text-[#E63946]">
          Complete Car Transformation · 0{step} / 06
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-[.9] mt-4">
          DON'T JUST MODIFY.
          <br />
          <span className="text-[#E63946]">TRANSFORM IT.</span>
        </h1>
        <p className="text-white/60 mt-6 max-w-2xl">
          Give us your car. Tell us your vision. We will shape a complete
          modification plan around your car, your requirements and your budget.
        </p>
      </div>
      <form
        onSubmit={submit}
        className="mt-12 bg-[#111] border border-white/10 p-5 md:p-10"
      >
        {step === 1 && (
          <fieldset className="space-y-4">
            <legend className="font-display text-3xl mb-6">CAR DETAILS</legend>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["name", "Name"],
                ["phone", "Phone number"],
                ["email", "Email"],
                ["car_brand", "Car brand"],
                ["car_model", "Car model"],
                ["model_year", "Model year"],
                ["city", "City / location"],
              ].map(([key, placeholder]) => (
                <input
                  key={key}
                  required={
                    key === "name" || key === "phone" || key === "email"
                  }
                  type={key === "email" ? "email" : "text"}
                  className="sa-input"
                  placeholder={placeholder}
                  value={f[key] || ""}
                  onChange={(e) => set(key, e.target.value)}
                />
              ))}
            </div>
            <textarea
              className="sa-input"
              rows="4"
              placeholder="Current condition of your car"
              value={f.current_condition || ""}
              onChange={(e) => set("current_condition", e.target.value)}
            />
          </fieldset>
        )}
        {step === 2 && (
          <fieldset>
            <legend className="font-display text-3xl mb-2">
              WHAT DO YOU WANT?
            </legend>
            <p className="text-white/50 mb-6">
              Select every area that interests you.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {services.map((service) => (
                <label
                  key={service}
                  className={`border p-4 cursor-pointer ${f.services.includes(service) ? "border-[#E63946] text-white" : "border-white/15 text-white/60"}`}
                >
                  <input
                    type="checkbox"
                    className="mr-3 accent-[#E63946]"
                    checked={f.services.includes(service)}
                    onChange={(e) =>
                      set(
                        "services",
                        e.target.checked
                          ? [...f.services, service]
                          : f.services.filter((x) => x !== service),
                      )
                    }
                  />
                  {service}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {step === 3 && (
          <fieldset>
            <legend className="font-display text-3xl mb-6">
              DESCRIBE YOUR VISION
            </legend>
            <textarea
              required
              rows="10"
              className="sa-input"
              placeholder="Tell us how you want your car to look and/or perform."
              value={f.vision || ""}
              onChange={(e) => set("vision", e.target.value)}
            />
          </fieldset>
        )}
        {step === 4 && (
          <fieldset>
            <legend className="font-display text-3xl mb-6">YOUR BUDGET</legend>
            <div className="space-y-3">
              {budgets.map((budget) => (
                <label
                  key={budget}
                  className={`block border p-4 cursor-pointer ${f.budget === budget ? "border-[#E63946]" : "border-white/15"}`}
                >
                  <input
                    required
                    type="radio"
                    name="budget"
                    className="mr-3 accent-[#E63946]"
                    checked={f.budget === budget}
                    onChange={() => set("budget", budget)}
                  />
                  {budget}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {step === 5 && (
          <fieldset>
            <legend className="font-display text-3xl mb-2">
              SHOW US THE CAR
            </legend>
            <p className="text-white/50 mb-6">
              Upload current vehicle photos and optional inspiration images. You
              can add more later.
            </p>
            <label className="block border border-dashed border-white/25 p-8 text-center cursor-pointer">
              <span className="overline">UPLOAD CURRENT VEHICLE PHOTOS</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => files("vehicle_images", e)}
              />
            </label>
            <div className="flex gap-2 mt-4">
              {f.vehicle_images.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt="Vehicle preview"
                  className="w-20 h-20 object-cover"
                />
              ))}
            </div>
            <label className="block border border-dashed border-white/25 p-8 text-center cursor-pointer mt-6">
              <span className="overline">
                ADD INSPIRATION / REFERENCE IMAGES
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => files("inspiration_images", e)}
              />
            </label>
            <div className="flex gap-2 mt-4">
              {f.inspiration_images.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt="Inspiration preview"
                  className="w-20 h-20 object-cover"
                />
              ))}
            </div>
          </fieldset>
        )}
        {step === 6 && (
          <fieldset>
            <legend className="font-display text-3xl mb-6">
              READY TO START?
            </legend>
            <div className="border border-white/10 p-5 text-white/70 leading-relaxed">
              Your request will be reviewed by our team. Modifications and
              remapping are subject to vehicle suitability, technical assessment
              and applicable laws and regulations. We will discuss the right
              plan with you before any work begins.
            </div>
          </fieldset>
        )}
        <div className="flex justify-between gap-3 mt-10 pt-6 border-t border-white/10">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="sa-btn sa-btn-outline"
            >
              BACK
            </button>
          ) : (
            <span />
          )}
          {step < 6 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              className="sa-btn sa-btn-primary"
            >
              CONTINUE <ArrowRight size={16} />
            </button>
          ) : (
            <button disabled={saving} className="sa-btn sa-btn-primary">
              {saving ? "SENDING..." : "SUBMIT MY BUILD"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
